import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import { confirmAlert } from "react-confirm-alert";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { page, remove } from "./api-comunicado";
import ComunicadoFindDialog from "./ComunicadoFindDialog";
import ComunicadoForm from "./ComunicadoForm";
import ComunicadoStatusChip from "./ComunicadoStatusChip";
import ComunicadoTable from "./ComunicadoTable";
import ComunicadoTurmaTable from "../ComunicadoTurma/ComunicadoTurmaTable";
import ComunicadoAlunoTable from "../ComunicadoAluno/ComunicadoAlunoTable";
import ComunicadoUsuarioForm from "../ComunicadoUsuario/ComunicadoUsuarioForm";
import EngajamentoTable from "../ComunicadoUsuario/EngajamentoTable";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { successDefault } from "../../../util/ToastUtil";
import Container from "../../UI/Container";

const EMPTY_PAGE = { content: [], size: 0, totalElements: 0, number: 0 };

export default function ComunicadoContainer() {
  const [comunicado, setComunicado] = useState(null);
  const [comunicados, setComunicados] = useState(EMPTY_PAGE);
  const [pageNumber, setPageNumber] = useState(0);
  // Incrementado para forçar o recarregamento da página atual (após criar/remover).
  const [reloadKey, setReloadKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [preview, setPreview] = useState(false);
  const [openSearchComunicado, setOpenSearchComunicado] = useState(false);
  const usuario = useSelector((state) => state.usuario);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    page(pageNumber)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setComunicados(data);
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [pageNumber, reloadKey]);

  useEffect(() => {
    if (!comunicado) {
      setPreview(false);
    }
  }, [comunicado]);

  const handleChangePage = (event, newPage) => {
    setPageNumber(newPage);
  };

  const handleNovo = () => {
    setComunicado({ eNovo: true, dataHoraPara: new Date() });
  };

  const handleProcurar = () => {
    setComunicado(null);
    setOpenSearchComunicado(true);
  };

  const handleSaved = useCallback((data, eNovo) => {
    if (eNovo) {
      setPageNumber(0);
      setReloadKey((key) => key + 1);
      return;
    }
    setComunicados((prevState) => ({
      ...prevState,
      content: prevState.content.map((ps) => (ps.codigo === data.codigo ? data : ps)),
    }));
  }, []);

  const removeComunicado = (codComunicado) => {
    remove(codComunicado).then((data) => {
      if (data?.error) {
        errorMessage(data.message);
        return;
      }
      setComunicado(null);
      // Se o item removido era o único da página, volta uma página.
      if (comunicados.content.length === 1 && pageNumber > 0) {
        setPageNumber((p) => p - 1);
      } else {
        setReloadKey((key) => key + 1);
      }
      successDefault();
    });
  };

  const handleDelete = (codComunicado) => {
    confirmAlert({
      title: "Alerta",
      message: "Deseja deletar este comunicado?",
      buttons: [
        { label: "Sim", onClick: () => removeComunicado(codComunicado) },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  const hasDestinatariosTable =
    comunicado?.codigo && (comunicado.enviarPor === "Turma" || comunicado.enviarPor === "Aluno");

  const renderHeader = () => (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "center" }}
      spacing={2}
      mb={3}
    >
      {comunicado ? (
        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
          <Tooltip title="Voltar para a lista">
            <IconButton onClick={() => setComunicado(null)} edge="start">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box minWidth={0}>
            <Typography variant="h6" noWrap>
              {comunicado.eNovo ? "Novo Comunicado" : comunicado.titulo || "Comunicado"}
            </Typography>
            {comunicado.codigo && (
              <ComunicadoStatusChip
                comunicado={comunicado}
                comunicadoAprovacao={usuario.instituicao.comunicadoAprovacao}
              />
            )}
          </Box>
        </Stack>
      ) : (
        <Box>
          <Typography variant="h6">Comunicados</Typography>
          <Typography variant="body2" color="text.secondary">
            Crie, envie e acompanhe os comunicados da instituição.
          </Typography>
        </Box>
      )}

      <Stack direction="row" spacing={1} alignItems="center">
        {comunicado ? (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={preview ? "preview" : "form"}
            onChange={(event, value) => value && setPreview(value === "preview")}
          >
            <ToggleButton value="form">Formulário</ToggleButton>
            <ToggleButton value="preview">Pré-Visualização</ToggleButton>
          </ToggleButtonGroup>
        ) : (
          <>
            <Button variant="outlined" startIcon={<SearchIcon />} onClick={handleProcurar}>
              Procurar
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleNovo}>
              Novo Comunicado
            </Button>
          </>
        )}
        <Tooltip title="Mais opções">
          <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem component={Link} to="/comunicado-categoria">
            <ListItemIcon>
              <CategoryOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Categorias
          </MenuItem>
          <MenuItem component={Link} to="/comunicado-banner">
            <ListItemIcon>
              <ImageOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Banners
          </MenuItem>
        </Menu>
      </Stack>
    </Stack>
  );

  const renderDetail = () => (
    <Stack spacing={3}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: hasDestinatariosTable ? 8 : 12 }}>
          {preview ? (
            <ComunicadoUsuarioForm comunicadoUsuario={{ comunicado: comunicado }} />
          ) : (
            <ComunicadoForm
              comunicado={comunicado}
              setComunicado={setComunicado}
              onSaved={handleSaved}
              handleDelete={handleDelete}
            />
          )}
        </Grid>
        {hasDestinatariosTable && (
          <Grid size={{ xs: 12, md: 4 }}>
            {comunicado.enviarPor === "Turma" ? (
              <ComunicadoTurmaTable comunicado={comunicado} />
            ) : (
              <ComunicadoAlunoTable comunicado={comunicado} />
            )}
          </Grid>
        )}
      </Grid>
      {comunicado.codigo && comunicado.enviado && <EngajamentoTable comunicado={comunicado} />}
    </Stack>
  );

  return (
    <Container>
      <ComunicadoFindDialog
        openSearchComunicado={openSearchComunicado}
        setOpenSearchComunicado={setOpenSearchComunicado}
        setComunicado={setComunicado}
      />
      {renderHeader()}
      {comunicado ? (
        renderDetail()
      ) : (
        <ComunicadoTable
          comunicados={comunicados}
          isLoading={isLoading}
          handleChangePage={handleChangePage}
          setComunicado={setComunicado}
        />
      )}
    </Container>
  );
}
